import React from "react";
import { FaArrowUp } from "react-icons/fa";
import evenessLogo from "../assets/eveness-logo.png";

const Footer = () => {
  const policyLinks = [
    { label: "Shipping Policy", href: "/policies/shipping-policy" },
    { label: "Return & Refund", href: "/policies/return-refund" },
    { label: "Disclaimer", href: "/disclaimer" },
    { label: "Privacy Policy", href: "/policies/privacy-policy" },
    { label: "Payment Policy", href: "/policies/payment-policy" },
    { label: "Terms of Service", href: "/policies/terms-of-service" },
    { label: "Fraud & Spam Policy", href: "/policies/fraud-spam-policy" },
  ];

  const customerCareLinks = [
    { label: "Contact Us", href: "/contact-us" },
    { label: "About Us", href: "/about-us" },
    { label: "FAQs", href: "/faqs" },
    { label: "Track Your Order", href: "/track-your-order" },
  ];

  const servicesLinks = [
    { label: "Collection", href: "/shop" },
    { label: "Marketplace", href: "/marketplace" },
    { label: "Eftakhari Collection", href: "/eftakhari-collection" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const SectionTitle = ({ children }) => (
    <p className="font-['Poppins'] text-[10px] leading-[13px] tracking-[0.04em] font-medium text-[#D4D5D9]/60 uppercase mb-3">
      {children}
    </p>
  );

  const FooterLink = ({ href, children }) => (
    <a
      href={href}
      className="text-[#D4D5D9] hover:text-white hover:underline transition-colors duration-200"
    >
      {children}
    </a>
  );

  return (
    <footer className="bg-[#22272A] text-white mt-16">
      <div className="px-6 pt-10 pb-6 md:px-8 lg:px-12">
        <div className="mx-auto max-w-[1440px]">
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
            {/* Brand, Help and Careers */}
            <div className="sm:col-span-2 lg:col-span-3 flex flex-col gap-8">
              <div className="flex items-center gap-3">
                <img
                  src={evenessLogo}
                  alt="EVENESS Logo"
                  className="h-10 brightness-0 invert"
                />
                <span className="font-['Gopher'] text-[27.13px] font-light">
                  EVENESS
                </span>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-1">
                <div className="text-sm text-gray-400">
                  <SectionTitle>Help</SectionTitle>
                  <p className="text-[#D4D5D9] leading-relaxed">
                    Our support team is available to assist you by email at{" "}
                    <a
                      href="mailto:support@eveness.ai"
                      className="underline hover:text-white transition-colors"
                    >
                      support@eveness.ai
                    </a>
                  </p>
                </div>

                <div className="text-sm text-gray-400">
                  <SectionTitle>Careers</SectionTitle>
                  <p className="text-[#D4D5D9] leading-relaxed">
                    Feel equipped to contribute to the Eveness mission?{" "}
                    <a
                      href="mailto:careers@eveness.ai"
                      className="underline hover:text-white transition-colors"
                    >
                      careers@eveness.ai
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className="lg:col-span-2">
              <SectionTitle>Services</SectionTitle>
              <ul className="text-sm space-y-2.5">
                {servicesLinks.map((link) => (
                  <li key={link.label}>
                    <FooterLink href={link.href}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Our Policies */}
            <div className="lg:col-span-2">
              <SectionTitle>Our Policies</SectionTitle>
              <ul className="text-sm space-y-2.5">
                {policyLinks.map((link) => (
                  <li key={link.label}>
                    <FooterLink href={link.href}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Customer Care */}
            <div className="lg:col-span-2">
              <SectionTitle>Customer Care</SectionTitle>
              <ul className="text-sm space-y-2.5">
                {customerCareLinks.map((link) => (
                  <li key={link.label}>
                    <FooterLink href={link.href}>{link.label}</FooterLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Address */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="space-y-7 text-sm text-[#D4D5D9]">
                <div>
                  <SectionTitle>Address</SectionTitle>
                  <p className="leading-relaxed">
                    1800 E Washington Blvd, Los Angeles, CA 90021 USA
                  </p>
                </div>

                <div>
                  <SectionTitle>Contacts</SectionTitle>
                  <div className="space-y-2">
                    <a
                      href="tel:+18188499559"
                      className="block hover:text-white transition-colors"
                    >
                      +1 8188499559
                    </a>

                    <a
                      href="mailto:Team@eveness.ai"
                      className="block hover:text-white transition-colors"
                    >
                      Team@eveness.ai
                    </a>
                  </div>
                </div>

                <div>
                  <SectionTitle>Working Hours</SectionTitle>
                  <div className="space-y-2">
                    <p className="leading-relaxed">
                      Monday- Friday 10:00 AM - 5:00 PM Pacific Standard Time
                      (GMT-8)
                    </p>
                    <p className="leading-relaxed">
                      Response Time: Within 24-48 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Scroll to top */}
            <div className="lg:col-span-1 flex lg:justify-end">
              <button
                type="button"
                onClick={scrollToTop}
                aria-label="Scroll to top"
                className="bg-gray-200 text-black p-3 rounded-full hover:bg-gray-300 transition flex items-center justify-center h-11 w-11"
              >
                <FaArrowUp />
              </button>
            </div>
          </div>

          {/* Bottom Copyright */}
          <div className="mt-10 pt-6 border-t border-white/10">
            <p className="text-gray-400 text-sm">© 2026 - Eveness</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
